import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useNoticeStore } from "../../store/useStore";
import Button from "../../components/common/Button";

const FormContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #fff8f2 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const FormCard = styled.div`
  width: 852px;
  min-height: 500px;
  background-color: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(210, 105, 30, 0.12);
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #d2691e 0%, #b85a1a 100%);
  }
`;

const FormHeader = styled.div`
  padding: 36px 48px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #fff8f2 0%, #ffffff 100%);
`;

const FormTitle = styled.h1`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 26px;
  color: #d2691e;
  margin: 0;
  letter-spacing: -0.5px;
`;

const FormBody = styled.div`
  flex: 1;
  padding: 40px 47px;
`;

const FormGroup = styled.div`
  margin-bottom: 30px;
`;

const Label = styled.label`
  display: block;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 20px;
  color: #7f8084;
  margin-bottom: 15px;
`;

const Description = styled.p`
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #000000;
  margin: 0 0 15px 0;
`;

const Input = styled.input`
  width: 100%;
  height: 55px;
  border: 1px solid #000000;
  padding: 16px;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  background-color: #ffffff;

  &::placeholder {
    color: #7f8084;
  }

  &:focus {
    outline: none;
    border-color: #8367ff;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 180px;
  border: 1px solid #000000;
  padding: 16px;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  background-color: #ffffff;
  resize: vertical;

  &::placeholder {
    color: #7f8084;
  }

  &:focus {
    outline: none;
    border-color: #8367ff;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  padding: 0 47px 40px 47px;
  display: flex;
  justify-content: flex-end;
`;

const NoticeForm = () => {
  const navigate = useNavigate();
  const { addNotice, isLoading, error, clearError } = useNoticeStore();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("공지사항 제목을 입력해주세요.");
      return;
    }

    if (!body.trim()) {
      alert("공지사항 내용을 입력해주세요.");
      return;
    }

    const result = await addNotice(
      JSON.stringify({
        title: title.trim(),
        body: body.trim(),
      })
    );

    if (result.success) {
      alert("공지사항이 등록되었습니다.");
      navigate("/notice");
    } else {
      alert(`공지사항 등록 실패: ${result.error}`);
    }
  };

  return (
    <FormContainer>
      <FormCard>
        <FormHeader>
          <FormTitle>공지사항 등록</FormTitle>
        </FormHeader>

        <FormBody>
          {error && (
            <div
              style={{
                background: "#fee",
                color: "#d00",
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #fcc",
              }}
            >
              {error}
            </div>
          )}

          <FormGroup>
            <Description>
              아래 텍스트필드에 공지사항 제목을 입력해 주세요.
            </Description>
            <Label htmlFor="title">공지사항 제목을 입력해 주세요.</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error) clearError();
              }}
              placeholder="공지사항 제목을 입력해 주세요..."
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Description>
              아래 텍스트필드에 공지사항 내용을 입력해 주세요.
            </Description>
            <Label htmlFor="body">공지사항 내용을 입력해 주세요.</Label>
            <TextArea
              id="body"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                if (error) clearError();
              }}
              placeholder="공지사항 내용을 입력해 주세요..."
              disabled={isLoading}
            />
          </FormGroup>
        </FormBody>

        <ButtonContainer>
          <Button
            type="submit"
            variant="primary"
            size="large"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "공지사항 등록 중..." : "공지사항 등록하기"}
          </Button>
        </ButtonContainer>
      </FormCard>
    </FormContainer>
  );
};

export default NoticeForm;
